import type { LDClient, LDContext } from "launchdarkly-js-client-sdk";

import { createLogger } from "@/logger";

const log = createLogger("feed");

const IDENTIFY_READY_TIMEOUT_MS = 10_000;

/**
 * Identifies a user in LaunchDarkly without raising on slow initialization.
 *
 * `LDClient#waitForInitialization` rejects with `LaunchDarklyTimeoutError` when
 * the client has not finished initializing within the allotted window, and the
 * SDK additionally logs the rejection through `console.error`, both of which
 * surface in observability for what is a recoverable condition. Instead we wait
 * on `waitUntilReady` (which never rejects) raced against a soft timeout. If
 * the client is not ready by then we still call `identify` — it issues its own
 * flag fetch against the new context and does not depend on the prior init —
 * and downgrade the log to a warning.
 */
export async function identifyLaunchDarklyUser(
  ldClient: LDClient,
  context: LDContext,
  accountId: string,
  readyTimeoutMs: number = IDENTIFY_READY_TIMEOUT_MS,
): Promise<void> {
  const ready = await waitForReadyOrTimeout(ldClient, readyTimeoutMs);
  if (!ready) {
    log.warn("LaunchDarkly client not ready before identify; proceeding without initial flags", { accountId });
  }

  try {
    await ldClient.identify(context);
    log.info("LaunchDarkly user identified successfully", { accountId });
  } catch (error) {
    log.error("Failed to identify user in LaunchDarkly:", error);
  }
}

function waitForReadyOrTimeout(ldClient: LDClient, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), timeoutMs);
    ldClient
      .waitUntilReady()
      .then(() => {
        clearTimeout(timer);
        resolve(true);
      })
      .catch(() => {
        clearTimeout(timer);
        resolve(false);
      });
  });
}
